import moment from 'moment'

/**
 * Calculate feature status for In-property messaging, Chatbot, and Announcements
 * Based on the same logic from generateFeatureStatusWithReason.js
 */
export const calculateFeatureStatuses = config => {
  if (!config || !config.subsidiary) {
    return {
      inPropertyMessaging: { status: 'Bad', reason: 'Data not available' },
      chatbot: { status: 'Bad', reason: 'Data not available' },
      announcements: { status: 'Bad', reason: 'Data not available' }
    }
  }

  const { subsidiary, phoneNumbers = [], subsidiaryServices = [] } = config

  // Helper checks
  const hasPhoneNumber = phoneNumbers.filter(p => p.status === 'Active').length > 0
  const hasPhoneNumberWithVoiceAndText =
    phoneNumbers.filter(p => p.status === 'Active' && p.capability === 'VoiceAndText')
      .length > 0

  const hasBasicService = subsidiaryServices.some(s => s.plan === 'Basic')
  const hasUnlimitedService = subsidiaryServices.some(s => s.plan === 'Unlimited')
  const hasCoreService = subsidiaryServices.some(s => s.plan === 'DirectfulCore')
  const hasMarketing = hasCoreService

  const isChurned = subsidiary.status === 'Churned'
  const isCampaignRegisteryComplete = subsidiary.tcrBrandId && subsidiary.tcrCampaignId
  const hasServiceStarted =
    subsidiary.serviceStartDate && moment().isAfter(moment(subsidiary.serviceStartDate))
  const enrichedGuestPercentage = subsidiary.enrichedGuestPercentage || 0
  const isPmsSyncing = subsidiary.isPmsSyncing || false
  const isChatBotEnabled = subsidiary.isChatBotEnabled || false
  const hasChatbotResponses = subsidiary.hasChatbotResponses || false
  const isUsingDefaultOpera =
    subsidiary.pms === 'operadefault' || subsidiary.pms === 'OperaDefault'

  // Calculate In-property messaging status
  const inPropertyMessaging = calculateInPropertyMessagingStatus({
    isPmsSyncing,
    isChurned,
    hasBasicService,
    hasUnlimitedService,
    hasPhoneNumber,
    isCampaignRegisteryComplete,
    hasServiceStarted,
    enrichedGuestPercentage,
    isUsingDefaultOpera
  })

  // Calculate Chatbot status
  const chatbot = calculateChatbotStatus({
    isChurned,
    hasPhoneNumber,
    isCampaignRegisteryComplete,
    hasBasicService,
    hasUnlimitedService,
    isChatBotEnabled,
    hasServiceStarted,
    enrichedGuestPercentage,
    hasChatbotResponses
  })

  // Calculate Announcements status
  const announcements = calculateAnnouncementsStatus({
    hasPhoneNumber,
    isCampaignRegisteryComplete,
    isChurned,
    enrichedGuestPercentage,
    hasServiceStarted,
    hasUnlimitedService
  })

  return {
    inPropertyMessaging,
    chatbot,
    announcements
  }
}

const calculateInPropertyMessagingStatus = ({
  isPmsSyncing,
  isChurned,
  hasBasicService,
  hasUnlimitedService,
  hasPhoneNumber,
  isCampaignRegisteryComplete,
  hasServiceStarted,
  enrichedGuestPercentage,
  isUsingDefaultOpera
}) => {
  if (!isPmsSyncing) {
    return {
      status: 'Bad',
      reason: 'PMS is not syncing'
    }
  }
  if (isChurned) {
    return {
      status: 'Bad',
      reason: 'Hotel is churned'
    }
  }
  if (!(hasBasicService || hasUnlimitedService)) {
    return {
      status: 'Warn',
      reason: 'Unlimited service is not enabled'
    }
  }
  if (!hasPhoneNumber) {
    return {
      status: 'Bad',
      reason: 'No phone number'
    }
  }
  if (!isCampaignRegisteryComplete) {
    return {
      status: 'Bad',
      reason: 'Campaign registery is not complete'
    }
  }
  if (!hasServiceStarted) {
    return {
      status: 'Bad',
      reason: 'Service has not started'
    }
  }
  if (enrichedGuestPercentage < 0.9) {
    return {
      status: 'Bad',
      reason: 'Subscribers are still processing'
    }
  }
  if (isUsingDefaultOpera) {
    return {
      status: 'Warn',
      reason: 'Using Opera default reports'
    }
  }
  return {
    status: 'Good'
  }
}

const calculateChatbotStatus = ({
  isChurned,
  hasPhoneNumber,
  isCampaignRegisteryComplete,
  hasBasicService,
  hasUnlimitedService,
  isChatBotEnabled,
  hasServiceStarted,
  enrichedGuestPercentage,
  hasChatbotResponses
}) => {
  if (isChurned) {
    return {
      status: 'Bad',
      reason: 'Hotel is churned'
    }
  }
  if (!hasPhoneNumber) {
    return {
      status: 'Bad',
      reason: 'No phone number'
    }
  }
  if (!isCampaignRegisteryComplete) {
    return {
      status: 'Bad',
      reason: 'Campaign registery is not complete'
    }
  }
  if (!(hasBasicService || hasUnlimitedService)) {
    return {
      status: 'Warn',
      reason: 'Unlimited service is not enabled'
    }
  }
  if (!isChatBotEnabled) {
    return {
      status: 'Bad',
      reason: 'Chatbot is not enabled'
    }
  }
  if (!hasServiceStarted) {
    return {
      status: 'Bad',
      reason: 'Service has not started'
    }
  }
  if (enrichedGuestPercentage < 0.9) {
    return {
      status: 'Bad',
      reason: 'Subscribers are still processing'
    }
  }
  if (!hasChatbotResponses) {
    return {
      status: 'Warn',
      reason: 'Chatbot responses are not set'
    }
  }
  return {
    status: 'Good'
  }
}

const calculateAnnouncementsStatus = ({
  hasPhoneNumber,
  isCampaignRegisteryComplete,
  isChurned,
  enrichedGuestPercentage,
  hasServiceStarted,
  hasUnlimitedService
}) => {
  if (!hasPhoneNumber) {
    return {
      status: 'Bad',
      reason: 'No phone number'
    }
  }
  if (!isCampaignRegisteryComplete) {
    return {
      status: 'Bad',
      reason: 'Campaign registery is not complete'
    }
  }
  if (isChurned) {
    return {
      status: 'Bad',
      reason: 'Hotel is churned'
    }
  }
  if (enrichedGuestPercentage < 0.9) {
    return {
      status: 'Bad',
      reason: 'Subscribers are still processing'
    }
  }
  if (!hasServiceStarted) {
    return {
      status: 'Bad',
      reason: 'Service has not started'
    }
  }
  if (!hasUnlimitedService) {
    return {
      status: 'Warn',
      reason: 'Unlimited service is not enabled'
    }
  }
  return {
    status: 'Good'
  }
}
